import { API_METHOD, BASE_API_URL } from "@/constants";
import { fetcher } from "@/lib";
import {
  AddThreatReviewUrlReportParams,
  ApproveRejectThreatReviewUrlReportParams,
  ThreatReviewCategory,
  ThreatReviewCategoryResponse,
  ThreatReviewStats,
  ThreatReviewUrlReportDetail,
  ThreatReviewUrlReportParams,
  ThreatReviewUrlReportResponse,
  UpdateThreatReviewUrlReportParams,
  UploadScamEvidenceParams,
  UploadScamEvidenceResponse,
} from "@/types";

// Get threat review url reports
export async function getThreatReviewUrlReports({
  offset = 0,
  limit = 10,
  status,
  search,
  source,
  priority,
  approvalStatus,
  isForApproval = false,
  allReport,
}: ThreatReviewUrlReportParams): Promise<ThreatReviewUrlReportResponse> {
  return fetcher(
    `${BASE_API_URL}/threat-review-url-reports?offset=${offset}&limit=${limit}&source=${source}&priority=${priority}&status=${status}&search=${search}&approvalStatus=${approvalStatus}&isForApproval=${isForApproval}&allReport=${allReport}`,
    { method: API_METHOD.GET }
  );
}

// Threat review stats by source
export async function getThreatReviewStatsBySource(): Promise<ThreatReviewStats> {
  return fetcher(`${BASE_API_URL}/threat-review-stats/by-source`, { method: API_METHOD.GET });
}

// Threat review url report detail
export async function getThreatReviewUrlReportDetail({
  reportId,
}: {
  reportId: string;
}): Promise<ThreatReviewUrlReportDetail> {
  return fetcher(`${BASE_API_URL}/threat-review-url-reports/${reportId}`, {
    method: API_METHOD.GET,
  });
}

// Update threat review url report
export async function updateThreatReviewUrlReport({
  reportId,
  reviewStatus,
  category,
  adminNotes,
  summary,
  keyAnalysisFactors,
  finalizedScore,
  email,
  is_submit_for_approval,
}: UpdateThreatReviewUrlReportParams): Promise<unknown> {
  return fetcher(`${BASE_API_URL}/threat-review-url-reports/${reportId}`, {
    method: API_METHOD.PUT,
    body: JSON.stringify({
      review_status: reviewStatus,
      category,
      admin_notes: adminNotes,
      summary,
      key_analysis_factors: keyAnalysisFactors,
      finalized_score: finalizedScore,
      email,
      is_submit_for_approval,
    }),
  });
}

// Update threat review url report
export async function approveRejectThreatReviewUrlReport({
  email,
  finalDecision,
  reportId,
  isApprovalAction,
}: ApproveRejectThreatReviewUrlReportParams): Promise<unknown> {
  return fetcher(`${BASE_API_URL}/threat-review-url-reports/approval/${reportId}`, {
    method: API_METHOD.PUT,
    body: JSON.stringify({ email, finalDecision, isApprovalAction }),
  });
}

// Add threat review url report
export async function addThreatReviewUrlReport({
  email,
  evidence,
  url,
  type,
  description,
  isByAdmin,
}: AddThreatReviewUrlReportParams): Promise<unknown> {
  return fetcher(`${BASE_API_URL}/threat-review-url-reports`, {
    method: API_METHOD.POST,
    body: JSON.stringify({
      url: decodeURIComponent(url),
      type,
      description,
      email,
      evidence,
      isByAdmin,
    }),
  });
}

// Get threat review categories
export async function getThreatReviewCategories(): Promise<ThreatReviewCategory[]> {
  return fetcher(`${BASE_API_URL}/threat-review-categories`, { method: API_METHOD.GET });
}

// Add threat review category
export async function addThreatReviewCategory({
  code,
  name,
  description = "",
}: {
  code: string;
  name: string;
  description?: string;
}): Promise<ThreatReviewCategoryResponse> {
  return fetcher(`${BASE_API_URL}/threat-review-categories`, {
    method: API_METHOD.POST,
    body: JSON.stringify({ code, name, description }),
  });
}

// Update threat review category
export async function updateThreatReviewCategory({
  categoryCode,
  name,
  description = "",
}: {
  categoryCode: string;
  name: string;
  description?: string;
}): Promise<ThreatReviewCategoryResponse> {
  return fetcher(`${BASE_API_URL}/threat-review-categories/${categoryCode}`, {
    method: API_METHOD.PUT,
    body: JSON.stringify({ name, description }),
  });
}

// Delete threat review category
export async function deleteThreatReviewCategory({
  categoryCode,
}: {
  categoryCode: string;
}): Promise<ThreatReviewCategoryResponse> {
  return fetcher(`${BASE_API_URL}/threat-review-categories/${categoryCode}`, {
    method: API_METHOD.DELETE,
  });
}

// Report suspicious website
export async function uploadScamEvidence({
  fileData,
  filename,
}: UploadScamEvidenceParams): Promise<UploadScamEvidenceResponse> {
  return fetcher(`${BASE_API_URL}/scam-evidence`, {
    method: API_METHOD.POST,
    body: JSON.stringify({
      fileData,
      filename,
    }),
  });
}

export async function updateUrlDetailInfo({
  reportId,
  aiAnalysis,
  basicInfo,
}: {
  reportId: string;
  aiAnalysis: any;
  basicInfo: any;
}): Promise<unknown> {
  return fetcher(`${BASE_API_URL}/threat-review-url-reports/url-detail-info/${reportId}`, {
    method: API_METHOD.PUT,
    body: JSON.stringify({
      ai_analysis: aiAnalysis,
      basic_info: basicInfo,
    }),
  });
}
